import React, { useEffect, useMemo, useRef, useState } from 'react';
import { string, func, arrayOf } from 'prop-types';
import { getFilteredMedia, tabs } from '../common';
import {
  Container,
  Title,
  TitleContainer,
  TabsContainer,
  IconButton,
  ButtonContainer,
  FullScreenContainer,
} from './styled';
import Tabs from '../../UI/Tabs';
import CloseIcon from '@material-ui/icons/Close';
import { IconButton as MUIIconButton } from '@material-ui/core';
import { Loading } from '../../UI/Loaders';
import MediaLibraryContent from '../Content';
import { MediaContainer } from '../styled';
import { Button } from '../../UI';
import { useGetAssets } from '../../../contexts/api/asset';

const normalizeMediaFilterValue = (value = '') =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const MediaLibrarySelector = ({
  onSelectMedia,
  onClose,
  showTypes,
  fullscreen = false,
  ...rest
}) => {
  const { assets, loading } = useGetAssets();

  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [selectedMedia, setSelectedMedia] = useState({});
  const [filterValue, setFilterValue] = useState('');
  const filterInputRef = useRef(null);

  const getTabs = () => {
    if (!showTypes.length) {
      return tabs;
    }

    return tabs.filter((tab) => showTypes.includes(tab.type));
  };

  useEffect(() => {
    const timerId = window.setTimeout(() => {
      filterInputRef.current?.focus();
    }, 0);

    return () => window.clearTimeout(timerId);
  }, []);

  const filteredMedia = useMemo(() => {
    const mediaForSelectedTab = getFilteredMedia(
      assets,
      getTabs()[selectedTabIndex]?.type,
    );

    const normalizedFilter = normalizeMediaFilterValue(filterValue.trim());

    if (!normalizedFilter) {
      return mediaForSelectedTab;
    }

    return mediaForSelectedTab.filter((mediaItem) => {
      const searchableName = normalizeMediaFilterValue(
        `${mediaItem?.name || ''} ${mediaItem?.filename || ''}`,
      );
      return searchableName.includes(normalizedFilter);
    });
  }, [assets, filterValue, selectedTabIndex, showTypes]);

  const contentJSX = (
    <>
      <TitleContainer>
        <Title>Media Library</Title>
        <IconButton aria-label="close" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </TitleContainer>
      <TabsContainer>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            flex: 1,
            minWidth: 0,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <Tabs
              tabTextColor="#424242"
              tabs={getTabs().map((tab) => tab.label)}
              onSelectTab={setSelectedTabIndex}
              selectedTabIndex={selectedTabIndex}
            />
          </div>
          <div
            style={{
              position: 'relative',
              display: 'inline-flex',
              alignItems: 'center',
              width: '260px',
              flexShrink: 0,
            }}
          >
            <input
              ref={filterInputRef}
              type="text"
              placeholder="Filter"
              value={filterValue}
              onChange={(event) => setFilterValue(event.target.value)}
              aria-label="Filter media"
              style={{
                width: '100%',
                paddingRight: '32px',
                boxSizing: 'border-box',
              }}
            />
            {filterValue ? (
              <MUIIconButton
                size="small"
                onClick={() => setFilterValue('')}
                style={{
                  position: 'absolute',
                  right: '4px',
                  padding: '4px',
                }}
                aria-label="Clear filter"
              >
                x
              </MUIIconButton>
            ) : null}
          </div>
        </div>
      </TabsContainer>
      <MediaContainer style={{ maxWidth: '100%' }}>
        {loading ? (
          <Loading />
        ) : (
          <MediaLibraryContent
            media={filteredMedia}
            onClickMedia={setSelectedMedia}
            isSelected={(m) => selectedMedia.id === m.id}
          />
        )}
      </MediaContainer>
      <ButtonContainer>
        <Button
          width={125}
          disabled={!selectedMedia.id}
          onClick={() => onSelectMedia(selectedMedia)}
        >
          OK
        </Button>
      </ButtonContainer>
    </>
  );

  return fullscreen ? (
    <FullScreenContainer>
      {contentJSX}
    </FullScreenContainer>
  ) : (
    <Container {...rest}>
      {contentJSX}
    </Container>
  );
};

MediaLibrarySelector.propTypes = {
  onSelectMedia: func.isRequired,
  onClose: func.isRequired,
  showTypes: arrayOf(string),
};

MediaLibrarySelector.defaultProps = {
  showTypes: [],
};

export default MediaLibrarySelector;
